import React, { useState } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { SystemPreset } from '../../lib/constants';
import {
  ArrowLeftRight,
  Sparkles,
  Zap,
  Battery,
  Sun,
  ShieldCheck,
  Check,
  Fuel,
  TrendingUp,
  AlertCircle,
  HelpCircle,
  Scale,
  Award,
  DollarSign
} from 'lucide-react';

interface MetricWithTooltipProps {
  label: string;
  tooltip: string;
}

const MetricWithTooltip: React.FC<MetricWithTooltipProps> = ({ label, tooltip }) => {
  return (
    <div className="group relative inline-flex items-center gap-1.5 cursor-help py-1">
      <span className="border-b border-dashed border-slate-600 group-hover:border-emerald-400 text-slate-200 group-hover:text-white transition-colors duration-200">
        {label}
      </span>
      <HelpCircle className="w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-400 transition-colors duration-200 shrink-0" />
      <div className="absolute left-0 top-full mt-2 w-72 sm:w-80 p-3 bg-slate-900 border border-slate-700/80 rounded-xl shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 transition-all duration-200 z-50 text-[11px] text-slate-300 font-normal leading-relaxed">
        <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-900 border-l border-t border-slate-700/80 transform rotate-45"></div>
        <div className="relative flex items-start gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
          <span>{tooltip}</span>
        </div>
      </div>
    </div>
  );
};

export const PackageComparison: React.FC = () => {
  const { selectPreset, setIsLeadModalOpen, presets } = useSolarStore();

  // Selected packages to compare
  const [packageAId, setPackageAId] = useState<string>('standard_3_5kva');
  const [packageBId, setPackageBId] = useState<string>('executive_7_5kva');

  // Interactive Economic Adjustments for custom ROI
  const [monthlyFuelSpend, setMonthlyFuelSpend] = useState<number>(150000);
  const [monthlyGridSpend, setMonthlyGridSpend] = useState<number>(45000);
  const [gridReliability, setGridReliability] = useState<number>(30); // 30% grid reliability (typical Nigeria)

  const packageA = presets.find(p => p.id === packageAId) || presets[1] || presets[0];
  const packageB = presets.find(p => p.id === packageBId) || presets[2] || presets[0];

  // Helper: Format Naira currency beautifully
  const formatNaira = (value: number) => {
    return `₦${Math.round(value).toLocaleString()}`;
  };

  // Dynamic payback period calculator
  // Total potential monthly displacement = Fuel displaced + Grid savings displaced
  const calculateDynamicPayback = (pkg: SystemPreset) => {
    // A larger system can displace more of the grid and generator bills.
    // Let's model the displacement percentage based on system capacity
    // 1.5kVA displaces ~40% of standard gen bill and ~50% grid bill
    // 3.5kVA displaces ~85% of standard gen bill and ~80% grid bill
    // 7.5kVA displaces ~100% of standard gen bill and ~95% grid bill
    // 10kVA+ displaces ~100% of standard gen bill and ~100% grid bill
    let genDisplacementFactor = 0.4;
    let gridDisplacementFactor = 0.5;

    if (pkg.capacityKva >= 10) {
      genDisplacementFactor = 1.0;
      gridDisplacementFactor = 1.0;
    } else if (pkg.capacityKva >= 7.5) {
      genDisplacementFactor = 1.0;
      gridDisplacementFactor = 0.95;
    } else if (pkg.capacityKva >= 3.5) {
      genDisplacementFactor = 0.85;
      gridDisplacementFactor = 0.80;
    }

    const monthlySavings = (monthlyFuelSpend * genDisplacementFactor) + (monthlyGridSpend * gridDisplacementFactor);
    const paybackMonths = monthlySavings > 0 ? pkg.priceNgn / monthlySavings : 120;
    return {
      monthlySavings,
      paybackMonths: Math.max(1, Math.round(paybackMonths * 10) / 10),
      yearlySavings: monthlySavings * 12
    };
  };

  const dynamicStatsA = calculateDynamicPayback(packageA);
  const dynamicStatsB = calculateDynamicPayback(packageB);

  // Quick preset updates for interactive pricing
  const applyEconomicPreset = (fuel: number, grid: number, reliability: number) => {
    setMonthlyFuelSpend(fuel);
    setMonthlyGridSpend(grid);
    setGridReliability(reliability);
  };

  const handleSelectPackage = (pkg: SystemPreset) => {
    selectPreset(pkg);
    setIsLeadModalOpen(true, `Quotation Request: ${pkg.name} (${pkg.capacityKva} kVA)`);
  };

  return (
    <div id="solar-system-comparison" className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl text-white space-y-8 my-10">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2">
            <Scale className="w-3.5 h-3.5 text-emerald-400 animate-pulse" /> Double Package Comparison Engine
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            Compare Solar Packages Side-by-Side
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Pick any two configurations to cross-reference technical capacities, appliance limits, and dynamic payback periods.
          </p>
        </div>

        {/* Quick Help Tip */}
        <div className="hidden lg:flex items-center gap-2.5 bg-slate-950 p-3 rounded-2xl border border-slate-800 text-xs text-slate-300 max-w-sm">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
          <span><strong>Quick Tip:</strong> Ensure your inverter kVA exceeds your peak simultaneous startup wattage (amps × voltage).</span>
        </div>
      </div>

      {/* Package Selectors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-950 p-5 rounded-2xl border border-slate-800/60">
        {/* System A Selector */}
        <div className="space-y-2">
          <label className="text-xs font-black uppercase text-slate-400 tracking-wider block">
            System Configuration A (Left Column)
          </label>
          <div className="relative">
            <select
              id="compare-select-a"
              value={packageAId}
              onChange={(e) => setPackageAId(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 hover:border-emerald-500 rounded-xl px-4 py-3.5 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 transition-all appearance-none cursor-pointer"
            >
              {presets.map((pkg) => (
                <option key={pkg.id} value={pkg.id} disabled={pkg.id === packageBId}>
                  {pkg.name} ({pkg.capacityKva} kVA • {pkg.batteryCapacityKwh} kWh Lithium)
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
              <ArrowLeftRight className="w-4 h-4 text-slate-400" />
            </div>
          </div>
        </div>

        {/* System B Selector */}
        <div className="space-y-2">
          <label className="text-xs font-black uppercase text-slate-400 tracking-wider block">
            System Configuration B (Right Column)
          </label>
          <div className="relative">
            <select
              id="compare-select-b"
              value={packageBId}
              onChange={(e) => setPackageBId(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 hover:border-emerald-500 rounded-xl px-4 py-3.5 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 transition-all appearance-none cursor-pointer"
            >
              {presets.map((pkg) => (
                <option key={pkg.id} value={pkg.id} disabled={pkg.id === packageAId}>
                  {pkg.name} ({pkg.capacityKva} kVA • {pkg.batteryCapacityKwh} kWh Lithium)
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
              <ArrowLeftRight className="w-4 h-4 text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Economical Parameters Panel */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <h4 className="text-sm font-black uppercase text-slate-300 tracking-wider flex items-center gap-2">
            <Fuel className="w-4 h-4 text-amber-400" /> 1. Customize Your Monthly Power Expenses (Nigeria-wide Benchmarks)
          </h4>
          <span className="text-[11px] text-slate-400">Payload shifts payback dynamic models below in real-time</span>
        </div>

        {/* Expense Sliders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-950 p-6 rounded-2xl border border-slate-800">
          {/* Fuel Spend Slider */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-bold">
              <span className="text-slate-400">Generator Fuel Spend:</span>
              <span className="text-amber-400">{formatNaira(monthlyFuelSpend)}/mo</span>
            </div>
            <input
              id="input-fuel-spend"
              type="range"
              min="20000"
              max="600000"
              step="10000"
              value={monthlyFuelSpend}
              onChange={(e) => setMonthlyFuelSpend(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>₦20,000</span>
              <span>₦600,000</span>
            </div>
          </div>

          {/* Grid Bill Spend Slider */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-bold">
              <span className="text-slate-400">NEPA / Grid Bill Spend:</span>
              <span className="text-sky-400">{formatNaira(monthlyGridSpend)}/mo</span>
            </div>
            <input
              id="input-grid-spend"
              type="range"
              min="5000"
              max="250000"
              step="5000"
              value={monthlyGridSpend}
              onChange={(e) => setMonthlyGridSpend(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>₦5,000</span>
              <span>₦250,000</span>
            </div>
          </div>

          {/* Grid Reliability Slider */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-bold">
              <span className="text-slate-400">Local Grid Reliability:</span>
              <span className="text-emerald-400">{gridReliability}% of Day</span>
            </div>
            <input
              id="input-grid-reliability"
              type="range"
              min="0"
              max="100"
              step="5"
              value={gridReliability}
              onChange={(e) => setGridReliability(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>0% (Full Outages)</span>
              <span>100% (Continuous)</span>
            </div>
          </div>
        </div>

        {/* Quick Expense Presets */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-[11px] text-slate-500 font-bold uppercase mr-1">Quick Load Profiles:</span>
          <button
            id="preset-btn-small"
            onClick={() => applyEconomicPreset(60000, 20000, 40)}
            className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700/60 text-[11px] font-bold text-slate-300 transition-all"
          >
            Moderate Flat (₦60k fuel + ₦20k NEPA)
          </button>
          <button
            id="preset-btn-medium"
            onClick={() => applyEconomicPreset(150000, 45000, 30)}
            className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700/60 text-[11px] font-bold text-slate-300 transition-all"
          >
            Standard Duplex (₦150k fuel + ₦45k NEPA)
          </button>
          <button
            id="preset-btn-large"
            onClick={() => applyEconomicPreset(350000, 110000, 20)}
            className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700/60 text-[11px] font-bold text-slate-300 transition-all"
          >
            Heavy / Mansion / Plaza (₦350k fuel + ₦110k NEPA)
          </button>
        </div>
      </div>

      {/* Main Double Comparison Table */}
      <div className="border border-slate-800 rounded-2xl overflow-hidden bg-slate-950 shadow-inner">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[640px]">
            {/* Table Header */}
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/50">
                <th className="p-4 sm:p-5 text-xs font-black uppercase text-slate-400 tracking-wider w-[30%]">Comparison Metrics</th>
                <th className="p-4 sm:p-5 text-center w-[35%] bg-slate-900/20 relative">
                  <div className="inline-block px-2.5 py-0.5 rounded-full bg-slate-800 text-[10px] text-emerald-400 font-bold mb-1 uppercase tracking-widest">
                    A • Left Focus
                  </div>
                  <div className="text-base sm:text-lg font-black text-white">{packageA.name}</div>
                  <div className="text-emerald-400 font-black text-sm mt-1">{formatNaira(packageA.priceNgn)}</div>
                </th>
                <th className="p-4 sm:p-5 text-center w-[35%] bg-emerald-500/5 relative">
                  <div className="absolute top-0 right-0 left-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                  <div className="inline-block px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-[10px] text-emerald-300 font-bold mb-1 uppercase tracking-widest border border-emerald-500/20">
                    B • Right Focus
                  </div>
                  <div className="text-base sm:text-lg font-black text-white">{packageB.name}</div>
                  <div className="text-emerald-400 font-black text-sm mt-1">{formatNaira(packageB.priceNgn)}</div>
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-slate-800/60 text-xs">
              {/* Category: Capacity & Specifications */}
              <tr className="bg-slate-900/30">
                <td colSpan={3} className="p-3 font-black text-emerald-400 uppercase tracking-widest text-[10px] bg-slate-900/60">
                  ⚡ Core Technical Specifications
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Inverter Capacity" 
                    tooltip="Determines the max simultaneous electrical load you can run. Higher kVA means more heavy appliances can power on at the same time." 
                  />
                </td>
                <td className="p-4 text-center text-slate-200">
                  <div className="font-bold text-white text-sm">{packageA.capacityKva} kVA</div>
                  <span className="text-[10px] text-slate-400">Pure Sine Wave ({packageA.inverterCapacityKw} kW load)</span>
                </td>
                <td className="p-4 text-center text-slate-200 bg-emerald-500/[0.01]">
                  <div className="font-bold text-white text-sm">{packageB.capacityKva} kVA</div>
                  <span className="text-[10px] text-slate-400">Pure Sine Wave ({packageB.inverterCapacityKw} kW load)</span>
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Peak Surge Capacity" 
                    tooltip="Motor-driven items (fridges, pumps, ACs) need up to 2x power for a split-second startup. Surge capacity prevents the system from tripping." 
                  />
                </td>
                <td className="p-4 text-center text-slate-200">
                  <span className="font-bold text-amber-400">{(packageA.capacityKva * 2).toFixed(1)} kVA</span>
                  <p className="text-[10px] text-slate-500">Handles heavy motor start induction</p>
                </td>
                <td className="p-4 text-center text-slate-200 bg-emerald-500/[0.01]">
                  <span className="font-bold text-amber-400">{(packageB.capacityKva * 2).toFixed(1)} kVA</span>
                  <p className="text-[10px] text-slate-500">Handles heavy motor start induction</p>
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Battery Life (Storage)" 
                    tooltip="Total usable energy stored in premium Lithium (LiFePO4) battery packs. Higher capacity ensures longer backup runtime during night hours or grid outages." 
                  />
                </td>
                <td className="p-4 text-center">
                  <div className="font-bold text-white text-sm flex items-center justify-center gap-1.5">
                    <Battery className="w-4 h-4 text-emerald-400 fill-emerald-400/20" />
                    {packageA.batteryCapacityKwh} kWh
                  </div>
                  <span className="text-[10px] text-slate-400">Grade-A LiFePO4 (6,000+ Cycles)</span>
                </td>
                <td className="p-4 text-center bg-emerald-500/[0.01]">
                  <div className="font-bold text-white text-sm flex items-center justify-center gap-1.5">
                    <Battery className="w-4 h-4 text-emerald-400 fill-emerald-400/20" />
                    {packageB.batteryCapacityKwh} kWh
                  </div>
                  <span className="text-[10px] text-slate-400">Grade-A LiFePO4 (6,000+ Cycles)</span>
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Solar PV Array Size" 
                    tooltip="The maximum generation power of your rooftop solar panels under full daylight. Larger solar arrays recharge batteries much faster on cloudy days." 
                  />
                </td>
                <td className="p-4 text-center">
                  <div className="font-bold text-white text-sm flex items-center justify-center gap-1.5">
                    <Sun className="w-4 h-4 text-amber-400" />
                    {packageA.solarPanelCapacityKw} kWp
                  </div>
                  <span className="text-[10px] text-slate-400">Mono-crystalline PERC array</span>
                </td>
                <td className="p-4 text-center bg-emerald-500/[0.01]">
                  <div className="font-bold text-white text-sm flex items-center justify-center gap-1.5">
                    <Sun className="w-4 h-4 text-amber-400" />
                    {packageB.solarPanelCapacityKw} kWp
                  </div>
                  <span className="text-[10px] text-slate-400">Mono-crystalline PERC array</span>
                </td>
              </tr>

              {/* Category: Financial Economics */}
              <tr className="bg-slate-900/30">
                <td colSpan={3} className="p-3 font-black text-emerald-400 uppercase tracking-widest text-[10px] bg-slate-900/60">
                  📈 Dynamic Economics & Payback Models (Adjusted to Inputs)
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Upfront Investment" 
                    tooltip="The complete package cost of high-grade hybrid equipment, full engineering design, mechanical railings, safe surge/lightning protection, and professional install." 
                  />
                </td>
                <td className="p-4 text-center">
                  <div className="text-base font-black text-white">{formatNaira(packageA.priceNgn)}</div>
                  {packageA.originalPriceNgn && (
                    <span className="text-[10px] text-slate-500 line-through">
                      {formatNaira(packageA.originalPriceNgn)}
                    </span>
                  )}
                </td>
                <td className="p-4 text-center bg-emerald-500/[0.01]">
                  <div className="text-base font-black text-white">{formatNaira(packageB.priceNgn)}</div>
                  {packageB.originalPriceNgn && (
                    <span className="text-[10px] text-slate-500 line-through">
                      {formatNaira(packageB.originalPriceNgn)}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Calculated Monthly Saving" 
                    tooltip="Your monthly utility bill & generator fuel budget fully replaced by free sunlight, serving as cash flow returning your upfront investment." 
                  />
                </td>
                <td className="p-4 text-center">
                  <div className="text-sm font-black text-emerald-400">+{formatNaira(dynamicStatsA.monthlySavings)}</div>
                  <span className="text-[10px] text-slate-400">Fuel & NEPA displaced</span>
                </td>
                <td className="p-4 text-center bg-emerald-500/[0.01]">
                  <div className="text-sm font-black text-emerald-400">+{formatNaira(dynamicStatsB.monthlySavings)}</div>
                  <span className="text-[10px] text-slate-400">Fuel & NEPA displaced</span>
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Projected Payback (ROI)" 
                    tooltip="The estimated count of months until your cumulative monthly fuel and NEPA power bill savings entirely equal your initial solar investment cost." 
                  />
                </td>
                <td className="p-4 text-center">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 text-sm font-black">
                    {dynamicStatsA.paybackMonths} Months
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">Break-even timeline</div>
                </td>
                <td className="p-4 text-center bg-emerald-500/5">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-black">
                    {dynamicStatsB.paybackMonths} Months
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">Break-even timeline</div>
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="5-Year Cumulative Savings" 
                    tooltip="Your net financial profit over a 5-year operating lifecycle after recovering 100% of the initial capital cost. Beyond this, your electricity is entirely free." 
                  />
                </td>
                <td className="p-4 text-center">
                  <div className="text-sm font-black text-white">{formatNaira(dynamicStatsA.yearlySavings * 5 - packageA.priceNgn)}</div>
                  <span className="text-[10px] text-slate-400">Net positive asset cash flow</span>
                </td>
                <td className="p-4 text-center bg-emerald-500/[0.01]">
                  <div className="text-sm font-black text-white">{formatNaira(dynamicStatsB.yearlySavings * 5 - packageB.priceNgn)}</div>
                  <span className="text-[10px] text-slate-400">Net positive asset cash flow</span>
                </td>
              </tr>

              {/* Category: Appliance & Load Limits */}
              <tr className="bg-slate-900/30">
                <td colSpan={3} className="p-3 font-black text-emerald-400 uppercase tracking-widest text-[10px] bg-slate-900/60">
                  🏠 Load & Home Appliances Support
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Best Recommended For" 
                    tooltip="Our official expert sizing recommendation matching your building type or commercial business layout to guarantee lifetime product health." 
                  />
                </td>
                <td className="p-4 text-center text-slate-300 font-medium leading-relaxed">
                  {packageA.recommendedFor}
                </td>
                <td className="p-4 text-center text-slate-300 font-medium leading-relaxed bg-emerald-500/[0.01]">
                  {packageB.recommendedFor}
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Covered Appliance Profile" 
                    tooltip="The list of typical home or business appliances that can run at the same time on this specific configuration without overload risks." 
                  />
                </td>
                <td className="p-4">
                  <div className="flex flex-col gap-1.5 items-center justify-center text-center">
                    {packageA.typicalAppliances.map((app, i) => (
                      <span key={i} className="px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 text-[10px] font-medium flex items-center gap-1 text-left w-full justify-start max-w-xs">
                        <Check className="w-3 h-3 text-emerald-400 shrink-0" /> {app}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="p-4 bg-emerald-500/[0.01]">
                  <div className="flex flex-col gap-1.5 items-center justify-center text-center">
                    {packageB.typicalAppliances.map((app, i) => (
                      <span key={i} className="px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 text-[10px] font-medium flex items-center gap-1 text-left w-full justify-start max-w-xs">
                        <Check className="w-3 h-3 text-emerald-400 shrink-0" /> {app}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
              <tr>
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Heavy Motors/ACs" 
                    tooltip="Shows if high-draw appliances like inverter air conditioners, water pumps, microwaves, or deep freezers are fully supported by this size inverter." 
                  />
                </td>
                <td className="p-4 text-center">
                  {packageA.capacityKva >= 3.5 ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400 font-bold bg-emerald-500/10 px-2 py-1 rounded-lg">
                      ✓ Supported (1-2 HP Inverter AC)
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-400 font-bold bg-amber-500/10 px-2 py-1 rounded-lg">
                      ⚠ Basic Fans Only (No heavy ACs)
                    </span>
                  )}
                </td>
                <td className="p-4 text-center bg-emerald-500/[0.01]">
                  {packageB.capacityKva >= 3.5 ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400 font-bold bg-emerald-500/10 px-2 py-1 rounded-lg">
                      ✓ Fully Supported (Up to 3 HP Inverter ACs)
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-400 font-bold bg-amber-500/10 px-2 py-1 rounded-lg">
                      ⚠ Basic Fans Only (No heavy ACs)
                    </span>
                  )}
                </td>
              </tr>

              {/* Action Buttons */}
              <tr className="bg-slate-900/50">
                <td className="p-4 font-bold text-slate-300">
                  <MetricWithTooltip 
                    label="Compare Action" 
                    tooltip="Locks in your choice of package and triggers the interactive quotation flow, sending a comprehensive quote or PDF draft." 
                  />
                </td>
                <td className="p-4 text-center">
                  <button
                    id={`select-pkg-btn-${packageA.id}`}
                    onClick={() => handleSelectPackage(packageA)}
                    className="bg-slate-800 hover:bg-slate-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs transition-all tracking-wide w-full max-w-[200px]"
                  >
                    Select {packageA.capacityKva}kVA Package
                  </button>
                </td>
                <td className="p-4 text-center bg-emerald-500/5">
                  <button
                    id={`select-pkg-btn-${packageB.id}`}
                    onClick={() => handleSelectPackage(packageB)}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black py-2.5 px-4 rounded-xl text-xs transition-all tracking-wide w-full max-w-[200px] shadow-lg shadow-emerald-500/15"
                  >
                    Select {packageB.capacityKva}kVA Package
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Educational Advisory Info Card */}
      <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
          <Award className="w-5 h-5" />
        </div>
        <div className="space-y-1 text-xs">
          <h5 className="font-bold text-white text-sm">How to select between {packageA.capacityKva}kVA and {packageB.capacityKva}kVA:</h5>
          <p className="text-slate-400 leading-relaxed">
            If your goal is purely lighting, refrigeration, laptops and television, a <strong>{packageA.capacityKva} kVA</strong> system is extremely cost-efficient with a fast break-even window. However, if you need to run high-start load motors such as pumping machines, inverter microwave ovens, or active air conditioning units, selecting the <strong>{packageB.capacityKva} kVA</strong> system protects against potential overload trips and secures expandable lithium power reserve.
          </p>
        </div>
      </div>
    </div>
  );
};
